<template>
    <ToastContainer />

    <AddRemoveUserModal
        :is-open="isAddRemoveUserModalOpen"
        :initial-user-id="columnDisplay.userId"
        :initial-email="columnDisplay.email"
        @close="closeAddRemoveUserModal"
        @save="handleColumnDisplaySave"
        @cancel="closeAddRemoveUserModal"
    />
 
    <div class="search-team-and-schedule-page main-container">
        <Hero
            :title="pageTitle"
            :subtitle="pageSubtitle"
        >
            <template #breadcrumb>
                <div style="display: none;">
                    Hidden breadcrumb
                </div>
            </template>
            <template #actions>
                <PagePreferenceSelector
                    @view-changed="handleViewChanged"
                    @view-loaded="handleViewLoaded"
                />
            </template>
        </Hero>
        <div class="search-controls">
            <!-- Button Toggle Bar -->
            <div
                class="toggle-bar"
                role="tablist"
                aria-label="View selection"
            >
                <button
                    type="button"
                    role="tab"
                    class="toggle"
                    :class="{ active: selectedView === 'table' }"
                    :aria-selected="selectedView === 'table'"
                    :aria-controls="'table-view-panel'"
                    @click="selectView('table')"
                    @keydown.enter="selectView('table')"
                    @keydown.space.prevent="selectView('table')"
                >
                    <span
                        class="toggle-text"
                        :class="{ inactive: selectedView !== 'table' }"
                    >Table</span>
                </button>

                <button
                    type="button"
                    role="tab"
                    class="toggle"
                    :class="{ active: selectedView === 'timeline' }"
                    :aria-selected="selectedView === 'timeline'"
                    :aria-controls="'timeline-view-panel'"
                    @click="selectView('timeline')"
                    @keydown.enter="selectView('timeline')"
                    @keydown.space.prevent="selectView('timeline')"
                >
                    <span
                        class="toggle-text"
                        :class="{ inactive: selectedView !== 'timeline' }"
                    >Timeline</span>
                </button>
            </div>

            <!-- Inline progress indicator — tracks grid/shifts data only -->
            <Transition name="progress-fade">
                <div
                    v-if="gridProgressState.isLoading.value"
                    class="controls-progress-indicator"
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                >
                    <!-- Inner row: spinner + content -->
                    <div class="progress-inner">
                        <div
                            class="progress-spinner"
                            aria-hidden="true"
                        />
                        <div class="progress-content">
                            <span class="progress-label">{{ gridProgressState.message.value }}</span>
                            <template v-if="gridProgressState.showProgress.value">
                                <div
                                    class="progress-track"
                                    role="progressbar"
                                    :aria-valuenow="gridProgressState.percent.value"
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                    :aria-label="`Loading progress: ${gridProgressState.percent.value}%`"
                                >
                                    <div
                                        class="progress-fill"
                                        :style="{ width: gridProgressState.percent.value + '%' }"
                                    />
                                </div>
                                <span
                                    class="progress-percent"
                                    aria-hidden="true"
                                >{{ gridProgressState.percent.value }}%</span>
                            </template>
                        </div>
                    </div>
                </div>
            </Transition>

            <al-multi-select
                class="search-timezone-select"
                style="--font-family: 'Roboto';"
                :value="timezoneSelected"
                test-id="searchTimezone"
                min-selected="1"
                max-selected="1"
                size="small"
                variant="wfm"
                @change="onTimezoneChange"
            >
                <!-- eslint-disable -->
                <div slot="label" test-id="timezoneLabel">Timezone</div>
                <!-- eslint-enable -->
                <al-select-option
                    v-for="option in timezoneOptions"
                    :key="option.id"
                    :value="option.id"
                    :display-value="option.value"
                    :test-id="`searchTimezone-${option.id}`"
                >
                    {{ option.value }}
                </al-select-option>
            </al-multi-select>
        </div>
        <div class="search-counter-title">
            <al-button
                size="small"
                style="width: 82px;"
                class="filter-button"
                :aria-label="`${isFacetPanelVisible ? 'Hide' : 'Show'} filters panel. ${filtersCount} filter${filtersCount !== 1 ? 's' : ''} applied`"
                :aria-expanded="isFacetPanelVisible"
                @click="toggleFiltersPanel"
            >
                <span class="filters-text">Filters</span>
                <al-icon
                    class="filters-icon"
                    test-id="message-icon"
                    :icon="isFacetPanelVisible ? 'chevron-double-left' : 'chevron-double-right'"
                    aria-hidden="true"
                />
                <span class="filter-count">{{ filtersCount }}</span>
            </al-button>
            
            <ViewByDropdown
                v-if="selectedView !== 'timeline'"
                v-model="colorMode"
                @update:model-value="handleColorModeChangeAccessible"
            />
            
            <GroupByDropdown
                v-model="groupBy"
                @update:model-value="handleGroupByChangeAccessible"
            />
            
            <TimelineZoomControls
                v-if="selectedView === 'timeline'"
                @zoom-in="handleZoomIn"
                @zoom-out="handleZoomOut"
                @reset-to-fit="handleResetToFit"
            />
            
            <div>
                <!-- Additional functionality to be implemented later-->
                <!-- <DatePicker
                    ref="datepickerRef"
                    v-model="selectedDate"
                    :is-range-picker="false"
                    :enable-time-picker="false"
                    :show-multi-calendar="false"
                    :min-date="minSelectable"
                    :max-date="maxSelectable"
                    :label="'Select Date'"
                /> -->
            </div>
            
            <ActionDropdown
                class="search-actions"
                :is-export-disabled="isExportDisabled"
                :selected-view="selectedView"
                @export="exportCSV"
                @add-remove="openAddRemoveUserModal"
            />
        </div>

        <div class="search-team-and-schedule-container">
            <!-- Facet panel — loader is rendered inside FacetPanel between header and filters -->
            <div
                v-show="isFacetPanelVisible"
                class="facet-panel-wrapper"
            >
                <FacetPanel
                    ref="facetPanelRef"
                    :query="query"
                    :active-table="searchTypeSelected"
                    @update-query="handleFacetChange"
                    @visibility-changed="updatePagePreferences"
                    @filters-count-changed="handleFiltersCountChange"
                />
            </div>
            <TeamAndScheduleDataGrid
                :query="query"
                :active-table="searchTypeSelected"
                @update-total="updateTeamSchedule"
                :selected-view="selectedView"
                :color-mode="colorMode"
                :group-by="groupBy"
                :column-display="columnDisplay"
            />
        </div>
    </div>
</template>

<!-- eslint-disable max-lines -->
<script setup lang="ts">
import {
    onMounted, provide, watch, ref
} from 'vue';
import { Hero } from '@/components/Commons/UI/molecules';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-alpine.css';
import '@playvox/almendra/dist/assets/styles/ag-grid/_ag-theme-playvox.scss';
import '@playvox/almendra/dist/components/dropdown';
import '@playvox/almendra/dist/components/dropdown-item';
import '@playvox/almendra/dist/components/multiSelect';
import { TeamAndScheduleDataGrid, PagePreferenceSelector } from './UI/organisms';
import FacetPanel from './UI/organisms/facets/FacetPanel.vue';
import TimelineZoomControls from './UI/molecules/TimelineZoomControls.vue';
import { ToastContainer } from '@agyletime/vue-3-common/src/components/Toast';
import { useProvidePagePreferences } from '@agyletime/vue-3-common/src/composables/useProvidePagePreferences';
import AddRemoveUserModal from './UI/organisms/filters/AddRemoveUserModal.vue';
import ActionDropdown from './UI/molecules/ActionDropdown.vue';
import ViewByDropdown from './UI/molecules/ViewByDropdown.vue';
import GroupByDropdown from './UI/molecules/GroupByDropdown.vue';
import { useTeamSchedule } from './composables/useTeamSchedule';
import { useWeekStartDay } from './composables/useWeekStartDay';
import { useCurrentUser } from './composables/useCurrentUser';
import {
    exportCSV as exportCSVUtils, clearFilters as clearFiltersUtils, buildInitialQuery
} from './utils/teamScheduleUtils';
import { Query } from './types/definitionsType';

const { weekStartDay, fetchWeekStartDay } = useWeekStartDay();
const pagePreferences = useProvidePagePreferences('teams_schedule_app', false, {
    preparePayloadBeforeSave: (data) => {
        // Remove exit flags that shouldn't be saved
        const dataRecord = data as Record<string, unknown>;
        const { exit: _exit, isExit: _isExit, ...cleanData } = dataRecord;
        return cleanData;
    }
});

const {
    pageTitle,
    pageSubtitle,
    searchTypeSelected,
    resetFilters,
    saveTeamScheduleGridSettings,
    restoreTeamScheduleGridSettings,
    exportCSVTeamAndSchedule,
    viewSelected,
    isLoadingView,
    selectedView,
    userTimezone,
    userTimeFormatPreference,
    query,
    facetPanelRef,
    isFacetPanelVisible,
    timezoneSelected,
    colorMode,
    groupBy,
    columnDisplay,
    isAddRemoveUserModalOpen,
    filtersCount,
    viewRefreshKey,
    isExportDisabled,
    timezoneOptions,
    handleFacetChange,
    handleViewChanged,
    handleViewLoaded,
    updateTeamSchedule,
    handleToggleChanged,
    updatePagePreferences,
    onTimezoneChange,
    openAddRemoveUserModal,
    closeAddRemoveUserModal,
    handleColumnDisplaySave,
    runOnMounted,
    toggleFiltersPanel,
    handleFiltersCountChange,
    setupDropdownCleanup
} = useTeamSchedule(pagePreferences, weekStartDay);

const exportCSV = () => {
    exportCSVUtils(searchTypeSelected, exportCSVTeamAndSchedule);
};

const handleColorModeChangeAccessible = (value: string) => {
    colorMode.value = value as 'TEMPLATES' | 'LOCATIONS' | 'NONE';
    updatePagePreferences();
};

const handleGroupByChangeAccessible = (value: string) => {
    groupBy.value = value as 'TEAMS' | 'NONE';
    updatePagePreferences();
};

const selectView = (view: 'table' | 'timeline') => {
    selectedView.value = view;
    handleToggleChanged(view);
};

const clearFilters = async () => {
    const { getCurrentUser } = useCurrentUser();
    const currentUserData = getCurrentUser();
    const userDataTimezone = currentUserData ? currentUserData.timezone : null;
    timezoneSelected.value = userDataTimezone || '';
    userTimezone.value = userDataTimezone || '';
    const urlQuery: Partial<Query> = {};
    query.value = buildInitialQuery(urlQuery, timezoneSelected.value, weekStartDay.value);
    updatePagePreferences();
    clearFiltersUtils({
        resetFilters, timezoneSelected, userTimezone
    });
};

const facetsLoading = ref(true);

// Progress state — owned here, written by DataGrid via inject, rendered in the status bar
const gridProgressState = {
    isLoading: ref(true),
    isInitialLoad: ref(true),
    message: ref('Loading data...'),
    showProgress: ref(false),
    percent: ref(0)
};

provide('resetFilters', resetFilters);
provide('saveTeamScheduleGridSettings', saveTeamScheduleGridSettings);
provide('restoreTeamScheduleGridSettings', restoreTeamScheduleGridSettings);
provide('userTimezone', userTimezone);
provide('userTimeFormatPreference', userTimeFormatPreference);
provide('exportCSVTeamAndSchedule', exportCSVTeamAndSchedule);
provide('viewSelected', viewSelected);
provide('isLoadingView', isLoadingView);
provide('clearFilters', clearFilters);
provide('facetsLoading', facetsLoading);
provide('gridProgressState', gridProgressState);

// Watch for changes that might update the dropdowns
watch([
    colorMode, groupBy, viewRefreshKey
], () => {
    setupDropdownCleanup();
}, { immediate: true });

type TimelineZoomAPI = {
    zoomIn: () => void;
    zoomOut: () => void;
    resetToFit: () => void;
} | null;

const timelineZoomAPI = ref<TimelineZoomAPI>(null);

provide('registerTimelineZoomAPI', (api: typeof timelineZoomAPI.value) => {
    timelineZoomAPI.value = api;
});

const handleZoomIn = () => {
    timelineZoomAPI.value?.zoomIn();
};const handleZoomOut = () => {
    timelineZoomAPI.value?.zoomOut();
};

const handleResetToFit = () => {
    timelineZoomAPI.value?.resetToFit();
};

onMounted(async () => {
    await fetchWeekStartDay();
    const timer = setInterval(() => {
        const usersData = sessionStorage.getItem('usersData');
        if (usersData) {
            clearInterval(timer);
            runOnMounted();
            // Setup dropdown cleanup after component is mounted
            setTimeout(setupDropdownCleanup, 100);
        }
    }, 300);
    
    // Also call it immediately in case everything is already loaded
    setupDropdownCleanup();
});

</script>

<style lang="scss">
@import './style.scss';
</style>
